import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';

export const SignupStatus = {
	EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
	QUESTIONNAIRE: 'QUESTIONNAIRE',
	AWAITING_PAYMENT: 'AWAITING_PAYMENT',
	PAYMENT_CONFIRMED: 'PAYMENT_CONFIRMED',
	ONBOARDING: 'ONBOARDING',
	COMPLETE: 'COMPLETE',
	EXPIRED: 'EXPIRED'
};

export const GroupPlan = {
	BASIC: 'BASIC',
	SMALL: 'SMALL',
	MEDIUM: 'MEDIUM',
	LARGE: 'LARGE',
	ENTERPRISE: 'ENTERPRISE',
	CUSTOM: 'CUSTOM'
};
export const useGroupSignupStore = defineStore('groupSignupStore', {
	state: () => ({
		// Session state
		sessionToken: null,
		status: null,
		currentStep: 1,
		groupNumber: null,

		// Email verification
		emailVerified: false,
		emailVerificationRequired: false,

		// Step 1: Basic Info
		basicInfo: {
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			groupName: '',
			street: '',
			city: '',
			state: '',
			country: '',
			zipcode: ''
		},

		// Step 2: Questionnaire
		questionnaire: {
			officialsCount: null,
			gamesPerSeason: null,
			leaguesDivisions: null,
			assignorsNeeded: null,
			adminsNeeded: null
		},

		// Step 3: Plan recommendation & selection
		planRecommendation: {
			recommendedPlan: null,
			estimatedPrice: null,
			availablePlans: []
		},
		selectedPlan: {
			plan: null,
			requestedUsers: null,
			requestedAssignors: null,
			requestedAdmins: null
		},

		// Step 4: Invoice/Payment
		invoice: {
			invoiceId: null,
			invoiceUrl: null,
			amount: null,
			status: null,
			canProceed: false
		},

		// Step 5-7: Onboarding
		onboarding: {
			shortName: '',
			description: '',
			timezone: ''
		},
		onboardingStep: 1,
		pendingInvitations: [],

		// UI state
		loading: false,
		error: null,

		// Admin: session list
		adminSessions: [],
		adminSessionsLoading: false
	}),

	getters: {
		/**
		 * Map status to wizard step (8-step flow)
		 * Step 1: Basic Info, Step 2: Email Verification, Step 3: Questionnaire,
		 * Step 4: Plan Selection, Step 5: Group Settings, Step 6: Team Invites,
		 * Step 7: Payment, Step 8: Complete
		 */
		stepFromStatus: (state) => {
			if (!state.status) return 1;

			switch (state.status) {
				case SignupStatus.EMAIL_VERIFICATION:
					return 2; // Email verification step
				case SignupStatus.QUESTIONNAIRE:
					// Determine sub-step based on data
					if (state.planRecommendation.recommendedPlan) return 4;
					if (state.questionnaire.officialsCount) return 3;
					return 3;
				case SignupStatus.AWAITING_PAYMENT:
					// Pre-payment onboarding steps (settings/team) or payment
					if ((state.onboardingStep || 1) >= 3) return 7;
					return 4 + (state.onboardingStep || 1);
				case SignupStatus.PAYMENT_CONFIRMED:
				case SignupStatus.ONBOARDING:
					return 8;
				case SignupStatus.COMPLETE:
					return 8;
				case SignupStatus.EXPIRED:
					return 0; // Special case
				default:
					return 1;
			}
		},

		/**
		 * Check if session is resumable
		 */
		isResumable: (state) => {
			return state.sessionToken && state.status && state.status !== SignupStatus.EXPIRED && state.status !== SignupStatus.COMPLETE;
		},

		/**
		 * Get resume URL for email
		 */
		resumeUrl: (state) => {
			if (!state.sessionToken) return null;
			return `${window.location.origin}/group-signup/${state.sessionToken}`;
		}
	},

	actions: {
		/**
		 * Start a new signup session
		 * POST /api/v1/group-signup/start
		 */
		async startSignup() {
			if (shouldThrottle('startSignup', 100)) {
				posthog.capture('throttled_action', { action: 'startSignup' });
				return { success: false, message: 'Too many requests. Please try again shortly.' };
			}

			this.loading = true;
			this.error = null;

			try {
				const payload = {
					firstName: this.basicInfo.firstName?.trim(),
					lastName: this.basicInfo.lastName?.trim(),
					email: this.basicInfo.email?.trim().toLowerCase(),
					password: this.basicInfo.password,
					groupName: this.basicInfo.groupName?.trim(),
					street: this.basicInfo.street?.trim() || null,
					city: this.basicInfo.city?.trim() || null,
					state: this.basicInfo.state?.trim() || null,
					country: this.basicInfo.country?.trim() || null,
					zipcode: this.basicInfo.zipcode?.trim() || null
				};

				const { data, status } = await apiClient.post('/group-signup/start', payload);

				if (status === 201 || status === 200) {
					this.sessionToken = data.sessionToken;
					this.emailVerificationRequired = data.emailVerificationRequired || false;
					this.status = this.emailVerificationRequired ? SignupStatus.EMAIL_VERIFICATION : SignupStatus.QUESTIONNAIRE;

					// SECURITY: Clear password from state after successful submission
					this.basicInfo.password = '';

					posthog.capture('group_signup_started', {
						groupName: this.basicInfo.groupName
					});

					return { success: true, sessionToken: data.sessionToken, emailVerificationRequired: this.emailVerificationRequired };
				}

				posthog.captureException('start_signup_unexpected_status', { status });
				return { success: false, message: 'Failed to start signup' };
			} catch (error) {
				this.error = error.response?.data?.message || error.response?.data?.error || 'Failed to start signup';
				posthog.captureException('start_signup_failed', {
					code: error?.code,
					status: error?.response?.status
				});
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Verify email with 6-digit code
		 * POST /api/v1/group-signup/{sessionToken}/verify-email
		 */
		async verifyEmail(code) {
			if (shouldThrottle('verifyEmail', 100)) {
				posthog.capture('throttled_action', { action: 'verifyEmail' });
				return { success: false, message: 'Too many requests. Please try again shortly.' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/verify-email`, {
					code: code
				});

				if (status === 200 && data.success) {
					this.emailVerified = true;
					this.status = SignupStatus.QUESTIONNAIRE;

					posthog.capture('group_signup_email_verified', {
						sessionToken: this.sessionToken
					});

					return { success: true };
				}

				return { success: false, message: data.error || 'Verification failed' };
			} catch (error) {
				this.error = error.response?.data?.error || 'Failed to verify email';
				posthog.captureException('email_verify_failed', { error: this.error });
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Resend email verification code
		 * POST /api/v1/group-signup/{sessionToken}/resend-verification
		 */
		async resendVerification() {
			if (shouldThrottle('resendVerification', 100)) {
				return { success: false, message: 'Too many requests. Please wait before requesting a new code.' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const { status } = await apiClient.post(`/group-signup/${this.sessionToken}/resend-verification`);

				if (status === 200) {
					posthog.capture('group_signup_verification_resent', {
						sessionToken: this.sessionToken
					});

					return { success: true, message: 'A new verification code has been sent to your email.' };
				}

				return { success: false, message: 'Failed to resend verification code' };
			} catch (error) {
				this.error = error.response?.data?.error || 'Failed to resend verification code';
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Get session state (for resuming)
		 * GET /api/v1/group-signup/{sessionToken}
		 */
		async getSession(token) {
			if (shouldThrottle('getSession', 50)) {
				return { success: false, message: 'Too many requests' };
			}

			this.loading = true;
			this.error = null;

			try {
				const { data, status } = await apiClient.get(`/group-signup/${token}`);

				if (status === 200) {
					this.restoreFromSession(data);
					posthog.capture('group_signup_resumed', { status: data.status });
					return { success: true, data };
				}

				return { success: false, message: 'Session not found' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to load session';
				if (error.response?.status === 404) {
					this.error = 'Session not found or expired';
				}
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Submit questionnaire
		 * POST /api/v1/group-signup/{sessionToken}/questionnaire
		 */
		async submitQuestionnaire() {
			if (shouldThrottle('submitQuestionnaire', 100)) {
				posthog.capture('throttled_action', { action: 'submitQuestionnaire' });
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const payload = {
					officialsCount: parseInt(this.questionnaire.officialsCount) || 0,
					gamesPerSeason: parseInt(this.questionnaire.gamesPerSeason) || 0,
					leaguesDivisions: parseInt(this.questionnaire.leaguesDivisions) || 0,
					assignorsNeeded: parseInt(this.questionnaire.assignorsNeeded) || 0,
					adminsNeeded: parseInt(this.questionnaire.adminsNeeded) || 0
				};

				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/questionnaire`, payload);

				if (status === 200) {
					this.planRecommendation = {
						recommendedPlan: data.recommendedPlan,
						estimatedPrice: data.estimatedPrice,
						availablePlans: data.availablePlans || []
					};

					posthog.capture('group_signup_questionnaire_submitted', {
						recommendedPlan: data.recommendedPlan
					});

					return { success: true, data };
				}

				return { success: false, message: 'Failed to submit questionnaire' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to submit questionnaire';
				posthog.captureException('questionnaire_submit_failed', { error: this.error });
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Select a plan
		 * POST /api/v1/group-signup/{sessionToken}/select-plan
		 */
		async selectPlan() {
			if (shouldThrottle('selectPlan', 100)) {
				posthog.capture('throttled_action', { action: 'selectPlan' });
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const payload = {
					selectedPlan: this.selectedPlan.plan,
					requestedUsers: this.selectedPlan.requestedUsers != null ? parseInt(this.selectedPlan.requestedUsers) : null,
					requestedAssignors: this.selectedPlan.requestedAssignors != null ? parseInt(this.selectedPlan.requestedAssignors) : null,
					requestedAdmins: this.selectedPlan.requestedAdmins != null ? parseInt(this.selectedPlan.requestedAdmins) : null
				};

				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/select-plan`, payload);

				if (status === 200) {
					// Only store amount and status from plan selection
					// Invoice URL and ID are set by generateInvoice()
					this.invoice.amount = data.amount;
					this.invoice.status = data.status;
					this.status = SignupStatus.AWAITING_PAYMENT;
					this.onboardingStep = 1;

					posthog.capture('group_signup_plan_selected', {
						plan: this.selectedPlan.plan,
						amount: data.amount
					});

					return { success: true, data };
				}

				return { success: false, message: 'Failed to select plan' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to select plan';
				posthog.captureException('plan_select_failed', { error: this.error });
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Generate invoice (regenerate if needed)
		 * POST /api/v1/group-signup/{sessionToken}/generate-invoice
		 */
		async generateInvoice() {
			if (shouldThrottle('generateInvoice', 100)) {
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/generate-invoice`);

				if (status === 200) {
					this.invoice = {
						invoiceId: data.invoiceId,
						invoiceUrl: data.invoiceUrl,
						amount: data.amount,
						status: data.status
					};

					return { success: true, data };
				}

				return { success: false, message: 'Failed to generate invoice' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to generate invoice';
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Check invoice status
		 * GET /api/v1/group-signup/{sessionToken}/invoice-status
		 */
		async checkInvoiceStatus() {
			if (shouldThrottle('checkInvoiceStatus', 50)) {
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			try {
				const { data, status } = await apiClient.get(`/group-signup/${this.sessionToken}/invoice-status`);

				if (status === 200) {
					this.invoice.status = data.status;
					this.invoice.canProceed = data.canProceed || false;
					return { success: true, data };
				}

				return { success: false, message: 'Failed to check invoice status' };
			} catch (error) {
				return { success: false, message: error.response?.data?.message || 'Failed to check status' };
			}
		},

		/**
		 * Confirm payment
		 * POST /api/v1/group-signup/{sessionToken}/confirm-payment
		 */
		async confirmPayment() {
			if (shouldThrottle('confirmPayment', 100)) {
				posthog.capture('throttled_action', { action: 'confirmPayment' });
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/confirm-payment`);

				if (status === 200 && data.confirmed) {
					this.status = SignupStatus.PAYMENT_CONFIRMED;
					this.onboardingStep = 4;

					posthog.capture('group_signup_payment_confirmed', {
						sessionToken: this.sessionToken
					});

					return { success: true, data };
				}

				// Payment not yet received
				this.error = data.message || 'Payment not yet confirmed. Please complete payment first.';
				return { success: false, message: this.error };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to confirm payment';
				posthog.captureException('payment_confirm_failed', { error: this.error });
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Update group settings (onboarding step 1)
		 * PATCH /api/v1/group-signup/{sessionToken}/onboarding/group-settings
		 */
		async updateGroupSettings() {
			if (shouldThrottle('updateGroupSettings', 100)) {
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const payload = {
					shortName: this.onboarding.shortName,
					description: this.onboarding.description,
					timezone: this.onboarding.timezone
				};

				const { data, status } = await apiClient.patch(`/group-signup/${this.sessionToken}/onboarding/group-settings`, payload);

				if (status === 200) {
					this.onboardingStep = 1;

					posthog.capture('group_signup_settings_updated', {
						sessionToken: this.sessionToken
					});

					return { success: true, data };
				}

				return { success: false, message: 'Failed to update settings' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to update settings';
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Add team invitations (onboarding step 2)
		 * POST /api/v1/group-signup/{sessionToken}/onboarding/invitations
		 */
		async addInvitations() {
			if (shouldThrottle('addInvitations', 100)) {
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				// Email validation regex
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				const payload = {
					invitations: this.pendingInvitations.filter((inv) => inv.email && emailRegex.test(inv.email.trim()))
				};

				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/onboarding/invitations`, payload);

				if (status === 200) {
					this.onboardingStep = 2;

					posthog.capture('group_signup_invitations_added', {
						count: this.pendingInvitations.length
					});

					return { success: true, data };
				}

				return { success: false, message: 'Failed to add invitations' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to add invitations';
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Complete onboarding
		 * POST /api/v1/group-signup/{sessionToken}/onboarding/complete
		 */
		async completeOnboarding() {
			if (shouldThrottle('completeOnboarding', 100)) {
				posthog.capture('throttled_action', { action: 'completeOnboarding' });
				return { success: false, message: 'Too many requests' };
			}

			if (!this.sessionToken) {
				return { success: false, message: 'No active session' };
			}

			this.loading = true;
			this.error = null;

			try {
				const { data, status } = await apiClient.post(`/group-signup/${this.sessionToken}/onboarding/complete`);

				if (status === 200) {
					this.status = SignupStatus.COMPLETE;
					this.groupNumber = data.groupNumber;

					posthog.capture('group_signup_completed', {
						groupNumber: data.groupNumber
					});

					return { success: true, groupNumber: data.groupNumber };
				}

				return { success: false, message: 'Failed to complete onboarding' };
			} catch (error) {
				this.error = error.response?.data?.message || 'Failed to complete onboarding';
				posthog.captureException('onboarding_complete_failed', { error: this.error });
				return { success: false, message: this.error };
			} finally {
				this.loading = false;
			}
		},

		// ==================== ADMIN ACTIONS ====================

		/**
		 * Get all active signup sessions (admin only)
		 * GET /api/v1/group-signup/admin/sessions
		 */
		async getAdminSessions() {
			if (shouldThrottle('getAdminSessions', 50)) {
				return { success: false, message: 'Too many requests' };
			}

			this.adminSessionsLoading = true;

			try {
				const { data, status } = await apiClient.get('/group-signup/employee/sessions');

				if (status === 200) {
					this.adminSessions = data.sessions || [];
					return { success: true, data };
				}

				return { success: false, message: 'Failed to fetch sessions' };
			} catch (error) {
				return { success: false, message: error.response?.data?.message || 'Failed to fetch sessions' };
			} finally {
				this.adminSessionsLoading = false;
			}
		},

		/**
		 * Get session details (admin only)
		 * GET /api/v1/group-signup/admin/sessions/{sessionToken}
		 */
		async getAdminSessionDetails(token) {
			if (shouldThrottle('getAdminSessionDetails', 50)) {
				return { success: false, message: 'Too many requests' };
			}

			try {
				const { data, status } = await apiClient.get(`/group-signup/employee/sessions/${token}`);

				if (status === 200) {
					return { success: true, data };
				}

				return { success: false, message: 'Session not found' };
			} catch (error) {
				return { success: false, message: error.response?.data?.message || 'Failed to fetch session' };
			}
		},

		/**
		 * Manually confirm payment (admin only)
		 * POST /api/v1/group-signup/{sessionToken}/admin/confirm-payment
		 */
		async adminConfirmPayment(token) {
			if (shouldThrottle('adminConfirmPayment', 100)) {
				return { success: false, message: 'Too many requests' };
			}

			this.loading = true;

			try {
				const { data, status } = await apiClient.post(`/group-signup/${token}/employee/confirm-payment`);

				if (status === 200) {
					posthog.capture('admin_payment_confirmed', {});
					return { success: true, data };
				}

				return { success: false, message: 'Failed to confirm payment' };
			} catch (error) {
				return { success: false, message: error.response?.data?.message || 'Failed to confirm payment' };
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Send resume link email to user (admin only)
		 * POST /api/v1/group-signup/{sessionToken}/admin/send-resume-email
		 */
		async adminSendResumeEmail(token) {
			if (shouldThrottle('adminSendResumeEmail', 100)) {
				return { success: false, message: 'Too many requests' };
			}

			try {
				const { data, status } = await apiClient.post(`/group-signup/${token}/employee/send-resume-email`);

				if (status === 200) {
					posthog.capture('admin_resume_email_sent', { sessionToken: token });
					return { success: true, data };
				}

				return { success: false, message: 'Failed to send email' };
			} catch (error) {
				return { success: false, message: error.response?.data?.error || 'Failed to send email' };
			}
		},

		/**
		 * Expire a signup session (admin only)
		 * POST /api/v1/group-signup/{sessionToken}/admin/expire
		 */
		async adminExpireSession(token) {
			if (shouldThrottle('adminExpireSession', 100)) {
				return { success: false, message: 'Too many requests' };
			}

			try {
				const { data, status } = await apiClient.post(`/group-signup/${token}/employee/expire`);

				if (status === 200) {
					posthog.capture('admin_session_expired', { sessionToken: token });
					return { success: true, data };
				}

				return { success: false, message: 'Failed to expire session' };
			} catch (error) {
				return { success: false, message: error.response?.data?.error || 'Failed to expire session' };
			}
		},

		// ==================== UTILITY ACTIONS ====================

		/**
		 * Restore state from session response
		 */
		restoreFromSession(sessionData) {
			this.sessionToken = sessionData.sessionToken;
			this.status = sessionData.status;
			this.groupNumber = sessionData.groupNumber;
			this.onboardingStep = sessionData.onboardingStep || 1;
			this.emailVerified = sessionData.emailVerified || false;

			if (sessionData.groupName) {
				this.basicInfo.groupName = sessionData.groupName;
			}

			// Restore questionnaire data from session
			if (sessionData.questionnaireData) {
				const qd = sessionData.questionnaireData;
				if (qd.officialsCount != null) this.questionnaire.officialsCount = qd.officialsCount;
				if (qd.gamesPerSeason != null) this.questionnaire.gamesPerSeason = qd.gamesPerSeason;
				if (qd.leaguesDivisions != null) this.questionnaire.leaguesDivisions = qd.leaguesDivisions;
				if (qd.assignorsNeeded != null) this.questionnaire.assignorsNeeded = qd.assignorsNeeded;
				if (qd.adminsNeeded != null) this.questionnaire.adminsNeeded = qd.adminsNeeded;

				// Restore onboarding fields
				if (qd.shortName) this.onboarding.shortName = qd.shortName;
				if (qd.description) this.onboarding.description = qd.description;
				if (qd.timezone) this.onboarding.timezone = qd.timezone;
			}

			if (sessionData.selectedPlan) {
				this.selectedPlan.plan = sessionData.selectedPlan;
			}

			if (sessionData.quotedPrice) {
				this.invoice.amount = sessionData.quotedPrice;
			}

			if (sessionData.pendingInvitations) {
				this.pendingInvitations = sessionData.pendingInvitations;
			}

			// Calculate wizard step from status
			this.currentStep = this.stepFromStatus;
		},

		/**
		 * Update basic info field
		 */
		updateBasicInfo(field, value) {
			if (field in this.basicInfo) {
				this.basicInfo[field] = value;
			}
		},

		/**
		 * Update questionnaire field
		 */
		updateQuestionnaire(field, value) {
			if (field in this.questionnaire) {
				this.questionnaire[field] = value;
			}
		},

		/**
		 * Add a pending invitation
		 */
		addPendingInvitation(invitation = { email: '', firstName: '', lastName: '', roles: [] }) {
			this.pendingInvitations.push(invitation);
		},

		/**
		 * Remove a pending invitation
		 */
		removePendingInvitation(index) {
			this.pendingInvitations.splice(index, 1);
		},

		/**
		 * Update a pending invitation
		 */
		updatePendingInvitation(index, field, value) {
			if (this.pendingInvitations[index]) {
				this.pendingInvitations[index][field] = value;
			}
		},

		/**
		 * Set current step (for navigation)
		 */
		setCurrentStep(step) {
			this.currentStep = step;
		},

		/**
		 * Reset entire store
		 */
		resetStore() {
			this.$reset();
		},

		/**
		 * Clear error
		 */
		clearError() {
			this.error = null;
		}
	}
});
